import { getFileVisual } from 'utils/fileVisuals'
import type { ReactNode } from 'react'
import {
  Award,
  BarChart2,
  Bell,
  BookOpen,
  Briefcase,
  Calendar,
  ClipboardList,
  Coffee,
  Crown,
  Eye,
  FileText,
  GraduationCap,
  Heart,
  HelpCircle,
  Image as ImageIcon,
  Lightbulb,
  Link2,
  MapPin,
  Megaphone,
  Package,
  Pencil,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Star,
  Store,
  Tag,
  Truck,
  Users,
  Video,
} from 'lucide-react'
import {
  DriveAccessLevel,
  DriveContextType,
  DriveNodeType,
  IDriveAccessEntry,
  IDriveNode,
  IDriveSearchNode,
} from 'config/driveTypes'
import dayjs from 'dayjs'
import { IoIosFolderOpen } from 'react-icons/io'
import keys from 'constants/keys'
import { IAudienceScope } from 'constants/interfaces'
import { DRIVE_DOWNLOAD_EXTENSIONS, FOLDER_ICON_BG, FOLDER_ICON_COLOR, MAX_FOLDER_DEPTH } from './constants'

// Default folder glyph (filled open folder) used when a folder has no custom icon-pack key.
export const FolderDefaultIcon = IoIosFolderOpen

// Per-node affordance gates from the caller's effective access_level (inherits down the folder tree).
// WRITE+: create sub-folder / upload / add link / rename. ADMIN+: delete / manage access / view-history.
// The app-level drive service permission caps the node level: without EDIT on the service the user is
// read-only everywhere, whatever their node grant says (the API enforces the same, returning 403).
export const canEditNode = (level?: DriveAccessLevel | null, drivePermission = 0) =>
  (level ?? 0) >= DriveAccessLevel.WRITE && drivePermission >= keys.USER_ACTIONS_MAP.EDIT
export const canAdminNode = (level?: DriveAccessLevel | null, drivePermission = 0) =>
  (level ?? 0) >= DriveAccessLevel.ADMIN && drivePermission >= keys.USER_ACTIONS_MAP.EDIT

// Roots/contents ordering: folders first (type asc: FOLDER→FILE→LINK), then alphabetical by name.
// Sort options exposed in the toolbar (newest/oldest by update time, name A-Z / Z-A).
export type SortMode = 'date_desc' | 'date_asc' | 'name_asc' | 'name_desc'

// Folders always sort before files; within that, by the chosen mode. Date uses updated_at_ts (fallback created).
export const sortDriveNodes = <
  T extends { type: DriveNodeType; name: string; updated_at_ts?: number | null; created_at_ts?: number }
>(
  nodes: T[],
  mode: SortMode = 'date_desc'
): T[] => {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  const ts = (n: T) => n.updated_at_ts || n.created_at_ts || 0
  const within: Record<SortMode, (a: T, b: T) => number> = {
    name_asc: byName,
    name_desc: (a, b) => byName(b, a),
    date_desc: (a, b) => ts(b) - ts(a) || byName(a, b),
    date_asc: (a, b) => ts(a) - ts(b) || byName(a, b),
  }
  return [...nodes].sort((a, b) => a.type - b.type || within[mode](a, b))
}

// Folder-icon pack: a folder's `icon` field stores the chosen key ('folder' is the default).
// Legacy folders may still hold an uploaded image URL instead — see isImageIcon.
export const FOLDER_ICON_OPTIONS: { key: string; Icon: any }[] = [
  { key: 'folder', Icon: IoIosFolderOpen },
  { key: 'users', Icon: Users },
  { key: 'file-text', Icon: FileText },
  { key: 'clipboard-list', Icon: ClipboardList },
  { key: 'book-open', Icon: BookOpen },
  { key: 'graduation-cap', Icon: GraduationCap },
  { key: 'megaphone', Icon: Megaphone },
  { key: 'calendar', Icon: Calendar },
  { key: 'store', Icon: Store },
  { key: 'shopping-cart', Icon: ShoppingCart },
  { key: 'bar-chart', Icon: BarChart2 },
  { key: 'briefcase', Icon: Briefcase },
  { key: 'shield', Icon: ShieldCheck },
  { key: 'package', Icon: Package },
  { key: 'truck', Icon: Truck },
  { key: 'image', Icon: ImageIcon },
  { key: 'video', Icon: Video },
  { key: 'link', Icon: Link2 },
  { key: 'tag', Icon: Tag },
  { key: 'star', Icon: Star },
  { key: 'heart', Icon: Heart },
  { key: 'lightbulb', Icon: Lightbulb },
  { key: 'bell', Icon: Bell },
  { key: 'award', Icon: Award },
  { key: 'settings', Icon: Settings },
  { key: 'map-pin', Icon: MapPin },
  { key: 'coffee', Icon: Coffee },
  { key: 'help-circle', Icon: HelpCircle },
]

const FOLDER_ICON_MAP = FOLDER_ICON_OPTIONS.reduce<{ [key: string]: any }>((acc, option) => {
  acc[option.key] = option.Icon
  return acc
}, {})

// Lucide component for a stored icon key; null when empty or a legacy image URL (render as <img>).
export const getFolderIconComponent = (icon?: string | null) => (icon && FOLDER_ICON_MAP[icon]) || null

// True for legacy folders whose icon is an uploaded image URL rather than a pack key.
export const isImageIcon = (icon?: string | null) => !!icon && /^https?:\/\//.test(icon)

// How the grid renders: as square tiles or as compact rows. Persisted per browser.
export type LibraryLayout = 'grid' | 'list'
const VIEW_MODE_STORAGE_KEY = 'library:viewMode'
export const getStoredLayout = (): LibraryLayout =>
  (typeof localStorage !== 'undefined' && localStorage.getItem(VIEW_MODE_STORAGE_KEY)) === 'list' ? 'list' : 'grid'
export const storeLayout = (layout: LibraryLayout) => {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, layout)
  } catch {
    // storage may be unavailable (private mode); the toggle still works in-memory
  }
}

// Chosen sort order, persisted per browser (defaults to newest-first).
const SORT_MODE_STORAGE_KEY = 'library:sortMode'
const SORT_MODES: SortMode[] = ['date_desc', 'date_asc', 'name_asc', 'name_desc']
export const getStoredSort = (): SortMode => {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(SORT_MODE_STORAGE_KEY) : null
  return (SORT_MODES as string[]).includes(stored || '') ? (stored as SortMode) : 'date_desc'
}
export const storeSort = (mode: SortMode) => {
  try {
    localStorage.setItem(SORT_MODE_STORAGE_KEY, mode)
  } catch {
    // storage may be unavailable (private mode); the control still works in-memory
  }
}

// Shared "add" affordance (New folder / Upload file / Add link) for the header `+` menu and empty-state tiles.
export interface ILibraryAddAction {
  key: string
  label: string // server translation key
  icon: ReactNode
  onClick: () => void
}

// Card title. Files drop a trailing extension (full name stays in the hover tooltip); folders and links keep theirs.
export const getDisplayName = (node: Pick<IDriveNode, 'type' | 'name'>) => {
  if (node.type !== DriveNodeType.FILE) return node.name
  const match = node.name.match(/^(.+)\.[A-Za-z0-9]{1,5}$/)
  return match ? match[1] : node.name
}

// Folder depth from `ancestors` (the chain above the node, self-excluded): root = 1, sub-folder = 2, …
export const getFolderDepth = (node?: IDriveNode | null) => (node ? (node.ancestors?.length || 0) + 1 : 0)

// A sub-folder may be created inside a folder only while doing so stays within the depth cap.
export const canCreateSubFolder = (folder?: IDriveNode | null) =>
  !!folder && folder.type === DriveNodeType.FOLDER && getFolderDepth(folder) < MAX_FOLDER_DEPTH

// Timestamps may arrive in seconds or milliseconds; normalize to seconds and format as a
// locale-aware short date ("Jul 5, 2026" — month name localizes per the loaded dayjs locale).
export const formatDate = (ts?: number, locale?: string) => {
  if (!ts) return ''
  const seconds = ts > 1e12 ? Math.floor(ts / 1000) : ts
  const d = dayjs.unix(seconds)
  return (locale ? d.locale(locale) : d).format('MMM D, YYYY')
}

// View records use the same epoch normalization.
export const formatViewTimestamp = formatDate

export const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`
}

// Maps a node to a lucide glyph + a soft pastel tile colour — one consistent icon family, with COLOUR as the
// type differentiator (e.g. doc = blue, pdf = red, txt = cyan, all the same document glyph). The icon renders
// in `color` on the `bg` tile (see NodeIcon / LibrarySearch).
export const getItemVisual = (
  node: Pick<IDriveNode, 'type' | 'mime_type'>
): { Icon: any; color: string; bg: string } => {
  if (node.type === DriveNodeType.LINK) {
    return { Icon: Link2, color: '#2563eb', bg: '#dbeafe' }
  }

  return getFileVisual(node.mime_type || '')
}

export const isImageNode = (node: IDriveNode) =>
  node.type === DriveNodeType.FILE && (node.mime_type || '').startsWith('image/')

export const isVideoNode = (node: IDriveNode) =>
  node.type === DriveNodeType.FILE && (node.mime_type || '').startsWith('video/')

// Media files (image/video) open in the in-app FsLightbox instead of a new browser tab.
export const isMediaNode = (node: IDriveNode) => isImageNode(node) || isVideoNode(node)

// Library-only: should this file be downloaded rather than opened in the Office web viewer? True for the
// extensions in DRIVE_DOWNLOAD_EXTENSIONS (Word docs) — see the constant for why.
export const shouldDownloadNotView = (node: IDriveNode) => {
  const ext = (node.name || '').split('.').pop()?.toLowerCase() || ''
  return DRIVE_DOWNLOAD_EXTENSIONS.includes(ext)
}

// Shape a minimal IDriveNode from a lean search projection so the shared node opener works on search hits.
export const toDriveNode = (result: IDriveSearchNode): IDriveNode => ({
  retail_id_node_id: result.node_id,
  type: result.type,
  parent_id: result.parent_id,
  name: result.name,
  ancestors: null,
  created_by: '',
  created_at_ts: 0,
  updated_at_ts: result.updated_at_ts,
  icon: result.icon,
  mime_type: result.mime_type,
  size_bytes: result.size_bytes,
  url: result.url,
})

// Visual for a lean search result — folders get the folder motif; files/links reuse getItemVisual.
export const getSearchNodeVisual = (node: IDriveSearchNode): { Icon: any; color: string; bg: string } => {
  if (node.type === DriveNodeType.FOLDER) return { Icon: IoIosFolderOpen, color: FOLDER_ICON_COLOR, bg: FOLDER_ICON_BG }
  return getItemVisual({ type: node.type, mime_type: node.mime_type } as IDriveNode)
}

// ─── Access / audience ──────────────────────────────────────────────────────
export interface IConfigForAccess {
  retailUsersObject?: {
    [uid: string]: {
      first_name?: string
      last_name?: string
      profile_img_url?: string
      avatar_profile_img_url?: string
    }
  } | null
  groups?: { [id: string]: { name?: string; images?: string[] } } | null
  tags?: { sk: string; name?: string; tag_color?: string }[] | null
}

export interface IAccessEntryVisual {
  name: string
  src?: string
  isTag: boolean
  tagColor?: string
}

// Resolve a grant into a display name + avatar/colour by cross-referencing config.
// Shared by the card audience stack and the Manage Access modal so both stay consistent.
export const getAccessEntryVisual = (entry: IDriveAccessEntry, config: IConfigForAccess): IAccessEntryVisual => {
  if (entry.context_type === DriveContextType.USER) {
    const user = config.retailUsersObject?.[entry.context_id]
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
    return { name: name || entry.context_id, src: user?.profile_img_url || user?.avatar_profile_img_url, isTag: false }
  }
  if (entry.context_type === DriveContextType.GROUP) {
    const group = config.groups?.[entry.context_id]
    return { name: group?.name || entry.context_id, src: group?.images?.[0], isTag: false }
  }
  const tag = config.tags?.find((t) => t.sk === entry.context_id)
  return { name: tag?.name || entry.context_id, isTag: true, tagColor: tag?.tag_color || '#94a3b8' }
}

// A grant belongs to the folder owner when it holds OWNER, or matches the node creator.
export const isOwnerAccessEntry = (entry: IDriveAccessEntry, createdBy?: string) =>
  entry.access_level === DriveAccessLevel.OWNER ||
  (entry.context_type === DriveContextType.USER && !!createdBy && entry.context_id === createdBy)

// Unread = the caller hasn't viewed it AND didn't create it — a user's own upload should never show as
// "unread" to them, even if the server hasn't flagged it viewed yet (e.g. a race right after upload).
export const isNodeUnread = (node: IDriveNode, myUid?: string): boolean =>
  node.is_viewed === false && node.created_by !== myUid

// Is this grant's context (a user or group id) within the viewer's org-tree scope? A null scope field means
// "don't filter" (e.g. no default_group). Used to hide out-of-tree grants from the Manage Access list — the
// API returns every grant on the node regardless of who's looking, so this filters purely on the client.
export const isEntryInDriveScope = (entry: IDriveAccessEntry, scope: IAudienceScope): boolean => {
  if (entry.context_type === DriveContextType.GROUP)
    return scope.groupIds === null || scope.groupIds.includes(entry.context_id)
  if (entry.context_type === DriveContextType.TAG)
    return scope.tagIds === null || scope.tagIds.includes(entry.context_id)
  return scope.userIds === null || scope.userIds.includes(entry.context_id)
}

// Colour + locale key + icon for the small access-level badge on a root card and the level menu rows.
export const getAccessLevelMeta = (level?: DriveAccessLevel): { labelKey: string; color: string; Icon: any } => {
  switch (level) {
    case DriveAccessLevel.OWNER:
      return { labelKey: 'owner', color: '#7c3aed', Icon: Crown }
    case DriveAccessLevel.ADMIN:
      return { labelKey: 'admin_admin_option', color: '#0891b2', Icon: ShieldCheck }
    case DriveAccessLevel.WRITE:
      return { labelKey: 'admin_edit_action', color: '#2563eb', Icon: Pencil }
    default:
      return { labelKey: 'admin_view_option', color: '#64748b', Icon: Eye }
  }
}

// Friendly kind for the card subtitle / table — returns a LOCALE KEY (render via LocaleText/useLocaleText),
// so it's translated like the rest of the row (a raw English "Spreadsheet" looked wrong in the RTL/Hebrew UI).
export const getFileKind = (node: Pick<IDriveNode, 'type' | 'mime_type'>): string => {
  if (node.type === DriveNodeType.LINK) return 'link'
  const mime = node.mime_type || ''
  if (mime === 'application/pdf') return 'file_pdf'
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return 'spreadsheet'
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'presentation'
  if (mime.includes('word') || mime.startsWith('text/')) return 'document'
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  return 'file'
}
